// Framework + libraries
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMutation, withDevtools, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
// Ours
import { withLoading } from '@r1-compass-library/store/_features/store-helpers';
import { IamUser, UsersIamDivisionChoice } from '@r1-compass-library/models';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalUsersStore } from '@r1-compass-library/store/users/globalUsersStore';
import { of } from 'rxjs';

export interface GlobalLoggedInUserStoreState {
    user: IamUser | undefined;
    selectedIamDivisionId: number | undefined;
    usersAvailableDivisions: UsersIamDivisionChoice[];
}

export const initialState: GlobalLoggedInUserStoreState = {
    user: undefined,
    selectedIamDivisionId: undefined,
    usersAvailableDivisions: [],
};

export const GlobalLoggedInUserStore = signalStore(
    { providedIn: 'root' },
    withDevtools('loggedInUser'),
    withState({ ...initialState }),
    withLoading(),
    withComputed((store, divisions = inject(GlobalDivisionsStore), userStore = inject(GlobalUsersStore)) => {
        const _selectedDivisionConfig = computed(() =>
            divisions.allDivisionConfigs().find(config => config.iamDivisionId === store.selectedIamDivisionId())
        );
        return {
            selectedDivisionConfig: _selectedDivisionConfig,
            selectedIamDivision: computed(() =>
                divisions.allIamDivisions().find(config => config.id === store.selectedIamDivisionId())
            ),
            // This is a bit of a weird field to be here, but we don't want a circular dep between this store and the divisions store
            userDefinedFields: computed(() => {
                return (
                    _selectedDivisionConfig()?.config?.mapping?.additionalFields?.filter(
                        field => field.source === 'User'
                    ) ?? []
                );
            }),
            selectedUserRole: computed(() => {
                const roles = userStore.roles();
                const usersDivisionMap = store
                    .user()
                    ?.iamUserDivisionMaps.find(
                        userDivMapping => userDivMapping.divisionId === store.selectedIamDivisionId()
                    );
                // ^^ So....the DB actually has a unique constraint on the divUserId. So right now there can be only 1 role per division. Which I did not know a couple days ago.
                // So really, you can just grab the first iamUserRoleDivMap record.
                // Index 0 or however you want to get it.
                // - August 29 2024
                const usersRoleId = usersDivisionMap?.iamUserRoleDivMaps.find(() => true)?.roleId;
                return roles.find(role => role.id === usersRoleId);
            }),
            selectedUserId: computed(() => {
                return store.user()?.id;
            }),
        };
    }),
    withMethods(store => ({
        // Useful to block on this
        setSelectedIamDivisionId: rxMutation({
            operation: (id: number | undefined) => of(id),
            onSuccess: id => patchState(store, { selectedIamDivisionId: id }),
        }),
        setUsersDivisions(divisionResponse: UsersIamDivisionChoice[]): void {
            patchState(store, { usersAvailableDivisions: divisionResponse });
        },
        addUserDivision(divisionResponse: UsersIamDivisionChoice): void {
            patchState(store, { usersAvailableDivisions: [...store.usersAvailableDivisions(), divisionResponse] });
        },
        setUser(iamUser: IamUser | undefined) {
            patchState(store, { user: iamUser });
        },
        resetState() {
            patchState(store, initialState);
        },
    })),
    withStorageSync({ key: 'loggedInUser', storage: () => localStorage })
);

export type GlobalLoggedInUserStore = InstanceType<typeof GlobalLoggedInUserStore>;
